import Icon from '@/components/icons/Icon';
import { classnames, roundRating } from '@/helpers';
import { customTwMerge } from '@/helpers/theme';
import { _s } from '@/locale';
import { Button } from '../../forms/buttons';

const baseTranslationKey = 'components.elements.Rating';

type Props = {
  avgRating: number;
  ratingCount?: number;
  reviewCount?: number;
  showCount?: boolean;
  size: 'sm' | 'md' | 'lg';
  className?: string;
  showLink?: boolean;
  showOnlyStars?: boolean;
  showOneStar?: boolean;
  onReviewsClick?: () => void;
};

const getStars = (rating: number, showOneStar?: boolean) => {
  let empty = false;
  if (showOneStar) {
    let fill = 100;
    return (
      <div className="w-md relative">
        <Icon variant="star-filled" color="black-300" style={{ width: 16, height: 16 }} />
        <Icon
          className="absolute inset-0"
          variant="star-filled"
          color="warning-500"
          style={{ width: 16, height: 16, clipPath: `inset(0 ${100 - fill}% 0 0)` }}
        />
      </div>
    );
  }
  return [1, 2, 3, 4, 5].map((i, key) => {
    let fill = 100;
    if (i > rating) {
      fill = !empty ? (rating % 1) * 100 : 0;
      empty = true;
    }
    return (
      <div key={key} className="w-md relative">
        <Icon variant="star-filled" color="black-300" style={{ width: 16, height: 16 }} />
        <Icon
          className="absolute inset-0"
          variant="star-filled"
          color="warning-500"
          style={{ width: 16, height: 16, clipPath: `inset(0 ${100 - fill}% 0 0)` }}
        />
      </div>
    );
  });
};

const getCountLabel = (ratingCount: number, reviewCount?: number) => {
  if (reviewCount > 0) {
    return _s('reviewsFromRatingsCounts', { reviews: reviewCount, ratings: ratingCount });
  }
  return ratingCount > 0
    ? `${ratingCount} ${_s(`${baseTranslationKey}.reviews`)}`
    : _s(`${baseTranslationKey}.noReviews`);
};

const Rating = ({
  avgRating,
  ratingCount,
  reviewCount,
  showCount,
  size = 'sm',
  className = '',
  showLink,
  showOnlyStars,
  showOneStar,
  onReviewsClick,
}: Props) => {
  const countLabel = getCountLabel(ratingCount, reviewCount);
  return (
    <div
      className={customTwMerge(
        `gap-xs inline-flex flex-row flex-wrap items-center justify-start ${size === 'sm' ? 'text-s' : 'text-l'}`,
        className,
      )}>
      <div className={`${size === 'lg' ? 'gap-sm' : 'gap-2xs'} flex select-none items-center`}>
        {size === 'lg' && (
          <>
            {ratingCount > 0 && (
              <span className="text-fg_primary text-h-xl flex items-center font-bold">
                {roundRating(avgRating, 1).toFixed(1)}
              </span>
            )}
            <span className={classnames('gap-2xs flex flex-col', ratingCount === 0 && 'items-end')}>
              <span className="gap-2xs flex">{getStars(avgRating, showOneStar)}</span>
              {!showLink && (
                <span className="text-m text-fg_secondary whitespace-nowrap font-normal">{countLabel}</span>
              )}
              {showLink && (
                <Button
                  onClick={onReviewsClick}
                  variant="link"
                  className="text-fg_link text-m whitespace-nowrap !p-0 underline">
                  {countLabel}
                </Button>
              )}
            </span>
          </>
        )}

        {size === 'md' && (
          <>
            {!showOnlyStars && ratingCount > 0 && (
              <span className={`text-fg_primary text-l flex items-center font-bold`}>
                {roundRating(avgRating, 1).toFixed(1)}
              </span>
            )}
            <span className="gap-2xs flex flex-col">
              <span className="gap-2xs flex">{getStars(avgRating, showOneStar)}</span>
            </span>
          </>
        )}

        {size === 'sm' && (
          <>
            {!showLink && <Icon variant="star-filled" color="warning-500" style={{ width: 16, height: 16 }} />}
            {ratingCount > 0 && (
              <span className={`text-fg_primary flex items-center font-bold ${!showLink ? 'text-s' : 'text-l'}`}>
                {roundRating(avgRating, 1).toFixed(1)}
              </span>
            )}
            {showLink && <Icon variant="star-filled" color="warning-500" style={{ width: 16, height: 16 }} />}
          </>
        )}
      </div>
      {(size === 'sm' || (size === 'md' && showCount)) && (
        <span
          className={classnames(
            /** dot styles */
            size === 'md' &&
              'before:w-xxs before:h-xxs before:mr-xs before:bg-fg_secondary before:inline-block before:rounded-full before:content-[""]',
            /** rest */
            `${size === 'sm' ? 'text-s' : 'text-l'} text-fg_secondary flex items-center`,
          )}>
          {showLink && (
            <Button
              onClick={onReviewsClick}
              variant="link"
              className={`text-fg_link text-l whitespace-nowrap !p-0 underline`}>
              {countLabel}
            </Button>
          )}
          {!showLink && (
            <span>
              {ratingCount > 0
                ? `${ratingCount} ${_s(`${baseTranslationKey}.reviews`)}`
                : _s(`${baseTranslationKey}.noReviews`)}
            </span>
          )}
        </span>
      )}
    </div>
  );
};

export default Rating;
